import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  Button,
  CircularProgress,
  Box,
  Divider,
  IconButton,
  DialogContentText,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import PageContainer from 'src/components/container/PageContainer';
import TextInputs from 'src/components/inputs/TextInputs';
import DashboardCard from 'src/components/shared/DashboardCard';
import { useMain } from 'src/views/hooks/useMain';
import ShowLoader from 'src/components/CustomComponents/ShowLoader';
import ClinetSuggestion from '../ClinetSuggestion';
import RadioInputs from 'src/components/inputs/RadioInputs';
import { useNavigate, useParams, useLocation } from 'react-router';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { maskData } from 'src/views/Billings/AddBill';
import { changeDateFormate } from 'src/utils/dateutils';

const PackageDetails = () => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [openDialogue, setOpenDialogue] = useState(false);
  const [openBillDialogue, setOpenBillDialogue] = useState(false);
  const [createdBill, setCreatedBill] = useState({});
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropDownIcon, setDropdownIcon] = useState(false);
  const [allClinetsData, setAllClinetsData] = useState([]);

  const { packageId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    fetchSinglePackageDetail,
    buyPackage,
    activeBranchId,
    setShowMessage,
    getBranch,
    branchData,
  } = useMain();

  // Initial form state
  const initialFormState = {
    clientNumber: '',
    packageId: packageId,
    paymentType: '',
    clientName: '',
    gender: '',
    paidAmount: data?.price,
    paidDues: 0,
  };

  const [formData, setFormData] = useState(initialFormState);
  const [clientSuggested, setClientSuggested] = useState([]);
  const [clientExists, setClientExists] = useState(true);
  const [showNewClientFields, setShowNewClientFields] = useState(false);
  const [hoveredItemId, setHoveredItemId] = useState(null);
  const TrimData = (data) => {
    if (data?.length < 20) return data;
    return data?.substring(0, 10) + data?.substring(data?.length - 10) + '...';
  };
  // Fetch package details
  const getSinglePackageData = async (packageId, activeBranchId) => {
    try {
      setLoading(true);
      const res = await fetchSinglePackageDetail(packageId, activeBranchId);
      if (res.statusCode === 200) {
        setData(res.data);
        // Initialize form with package price
        setFormData((prev) => ({
          ...prev,
          paidAmount: res.data.price,
          paidDues: 0,
        }));
      } else {
        setShowMessage({
          message: res.message || 'Error fetching package details',
          messageType: 'error',
        });
      }
    } catch (error) {
      setShowMessage({
        message: error.message || 'Something went wrong',
        messageType: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch branch data for client list
  const getBranchData = async (activeBranchId) => {
    try {
      const res = await getBranch(activeBranchId);
      if (res.statusCode === 200) {
        setAllClinetsData(res?.data?.client || []);
      }
    } catch (error) {
      setShowMessage({
        message: error.message || 'Something went wrong',
        messageType: 'error',
      });
    }
  };

  // Set client number from location state if available
  useEffect(() => {
    if (location.state?.clientNumber) {
      updateFormField('clientNumber', location.state.clientNumber);
    }
  }, [location.state]);

  // Initial data fetch
  useEffect(() => {
    if (activeBranchId && packageId) {
      getSinglePackageData(packageId, activeBranchId);
      getBranchData(activeBranchId);
    }
    return () => {
      setData({});
      setAllClinetsData([]);
    };
  }, [activeBranchId, packageId]);

  const updateFormField = (name, value) => {
    setFormData((prev) => {
      const updatedForm = { ...prev };

      if (name === 'paidAmount' && data.price) {
        let paid = Number(value) || 0;

        // Cap the amountPaid to data.price
        if (paid > data.price) {
          paid = data.price;
        }

        updatedForm.paidAmount = paid;
        updatedForm.paidDues = Math.max(0, data.price - paid);
      } else {
        updatedForm[name] = value;
      }

      return updatedForm;
    });

    // Update client suggestions when client number changes
    if (name === 'clientNumber') {
      updateClientSuggestions(value);
    }
  };

  // Update client suggestions based on input
  const updateClientSuggestions = (inputValue) => {
    const suggestions = allClinetsData.filter((client) =>
      client.clientNumber?.toLowerCase().includes(inputValue.toLowerCase()),
    );
    setClientSuggested(suggestions);
    setClientExists(suggestions.length > 0);
  };

  // Handle client selection from suggestions
  const handleSuggestedClick = (client) => {
    updateFormField('clientNumber', client.clientNumber);
    setClientSuggested([]);
  };

  // Dialog control functions
  const handleOpenDialogue = () => setOpenDialogue(true);
  const handleCloseDialogue = () => {
    setOpenDialogue(false);
    setShowNewClientFields(false);
    setClientExists(true);
    setFormData({
      ...initialFormState,
      paidAmount: data.price || 0,
      paidDues: 0,
    });
  };

  // Handle adding new client
  const handleAddNew = () => {
    setShowNewClientFields(true);
    setClientExists(false);
    updateFormField('clientName', '');
    updateFormField('gender', '');
  };

  // Handle package purchase
  const handleBuyPackage = async () => {
    const { clientNumber, paymentType, paidAmount } = formData;

    // Validation
    if (!clientNumber || clientNumber.length !== 10) {
      return setShowMessage({
        message: 'Valid 10-digit client number is required',
        messageType: 'error',
      });
    }

    if (!paymentType) {
      return setShowMessage({
        message: 'Payment type is required',
        messageType: 'error',
      });
    }

    // if (!paidAmount || isNaN(paidAmount)) {
    //   return setShowMessage({
    //     message: 'Valid amount is required',
    //     messageType: 'error',
    //   });
    // }

    const requestBody = {
      ...formData,
      packageId: data._id,
    };

    try {
      setLoading(true);
      const { amountPaid, ...rest } = requestBody;
      console.log(rest, 'rest');
      const res = await buyPackage(activeBranchId, rest);

      const message =
        res.statusCode === 200
          ? res.message || 'Package purchased successfully'
          : res.message || 'Error purchasing package';

      setShowMessage({
        message,
        messageType: res.statusCode === 200 ? 'success' : 'error',
      });

      if (res.statusCode === 200) {
        setCreatedBill(res?.data);
        setOpenBillDialogue(true);
        handleCloseDialogue();
      }
    } catch (error) {
      setShowMessage({
        message: error.message || 'Something went wrong',
        messageType: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBillCloseDialogue = () => {
    setCreatedBill({});
    setOpenBillDialogue(false);
  };

  // Package info display
  const renderPackageInfo = () => (
    <Box
      sx={{
        p: 2,
        mt: 2,
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        bgcolor: '#fafafa',
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography variant="subtitle1">
            Package: <strong>{data.packageName}</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mt: 1 }}>
            Duration: <strong>{data.packageDuration || data?.validTill}</strong>
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="subtitle1">
            Total Price: <strong>₹{data.price}</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mt: 1 }}>
            Dues: <strong>₹{formData.paidDues}</strong>
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );

  // Payment details section
  const renderPaymentSection = () => (
    <>
      <Divider sx={{ my: 1 }} />
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        Payment Details
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: 'black' }}>
              Payment Type *
            </Typography>
            <select
              className="form-select"
              name="paymentType"
              value={formData.paymentType || ''}
              onChange={(e) => updateFormField('paymentType', e.target.value)}
              style={{
                width: '100%',
                padding: '14px',
                marginTop: '0.8rem',
                fontSize: '14px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                backgroundColor: '#fff',
              }}
            >
              <option value="">-- Select Payment Method --</option>
              <option value="Cash">Cash</option>
              <option value="Credit/Debit Card">Credit/Debit Card</option>
              <option value="Paytm">Paytm</option>
              <option value="PhonePe">PhonePe</option>
              <option value="Gpay">Google Pay</option>
            </select>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextInputs
            title="Amount Paid"
            name="paidAmount"
            placeholder="Enter amount paid"
            value={formData.paidAmount}
            handleChange={(e) => updateFormField('paidAmount', e.target.value)}
            type="number"
            fullWidth
            inputProps={{
              min: 0,
              max: data.price,
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              p: 2,
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              bgcolor: '#f5f5f5',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="subtitle1">Amount Due:</Typography>
            <Typography variant="subtitle1" fontWeight="bold">
              ₹{formData.paidDues}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </>
  );
  console.log(data, 'Dataaaaaaaaaaa');

  return (
    <PageContainer title="Package Details" description="This is Package type membership details">
      <DashboardCard title="Package Details">
        <Grid container spacing={2}>
          {/* Package Info Section */}
          <Grid item xs={8}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body1">
                  Package Name: <strong>{data.packageName}</strong>
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body1">
                  Package price: <strong>₹{data.price}</strong>
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body1">
                  {/* Duration: <strong>{data.packageDuration || data?.validTill}</strong> */}
                      {data?.validTill ? "Validity" : "Duration"}: <strong>{data?.validTill ? `${changeDateFormate(data.validTill)}` : `${data?.packageDuration} Months`}</strong>
                </Typography>
              </Grid>
              {/* <Grid item xs={6}>
                <Typography variant="body1">
                  Services: 
                  <IconButton onClick={() => setDropdownIcon(!dropDownIcon)} size="small">
                    {dropDownIcon ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Typography>
                {dropDownIcon && (
                  <ul style={{
                    listStyle: 'none',
                    padding: '8px',
                    margin: 0,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    border: '1px solid #ddd',
                    borderRadius: '10px',
                    backgroundColor: '#FAFAFA'
                  }}>
                    {data.services?.map(service => (
                      <li key={service._id}>
                        <strong>{service.serviceName}</strong>
                      </li>
                    ))}
                  </ul>
                )}
              </Grid> */}
            </Grid>
          </Grid>

          {/* <Grid item xs={4} sx={{ textAlign: 'right' }}>
            <Button
              variant="contained"
              onClick={handleOpenDialogue}
              sx={{
                backgroundColor: '#6174DD',
                '&:hover': { backgroundColor: '#4a5abb' }
              }}
            >
              Buy Package
            </Button>
          </Grid> */}

          {/* Services Table */}
          <Grid item xs={12}>
            <table className="table-container mt-3">
              <thead className="table-thead">
                <tr>
                  {['Package Name', 'Package Services', 'Package Price'].map((h, index) => (
                    <th key={index} style={{ width: '33%' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="table-tbody">
                {data?.services?.map((service, index) => (
                  <tr key={service._id}>
                    {/* Service Name with Hover Tooltip */}
                    <td style={{ width: '33%', position: 'relative' }}>
                      <div
                        onMouseEnter={() => setHoveredItemId(service._id + '_serviceName')}
                        onMouseLeave={() => setHoveredItemId(null)}
                        style={{ cursor: 'pointer' }}
                      >
                        {TrimData(service?.serviceName)}

                        {hoveredItemId === service._id + '_serviceName' && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              backgroundColor: '#dfe3f8',
                              border: '1px solid #ccc',
                              padding: '8px',
                              borderRadius: '5px',
                              zIndex: 5,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                              whiteSpace: 'pre-wrap',
                              maxWidth: '300px',
                            }}
                          >
                            {service?.serviceName}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Other columns */}
                    <td style={{ width: '33%', position: 'relative' }}>{service?.category}</td>
                    <td style={{ width: '33%', position: 'relative' }}>{service?.price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Grid>
        </Grid>

        {/* Purchase Dialog */}
        <Dialog
          open={openDialogue}
          onClose={handleCloseDialogue}
          maxWidth="sm"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              width: '500px',
              minHeight: '76vh',
            },
          }}
        >
          <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600, bgcolor: '#f5f5f5' }}>
            Buy Package
          </DialogTitle>

          <DialogContent sx={{ py: 3 }}>
            <Typography variant="body1" color="text.secondary">
              Complete the following details to purchase package
            </Typography>

            {renderPackageInfo()}

            <Grid container spacing={3}>
              {/* Client Number Section */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <TextInputs
                      title="Client Number"
                      name="clientNumber"
                      placeholder="Enter client number"
                      value={formData.clientNumber}
                      handleChange={(e) => updateFormField('clientNumber', e.target.value)}
                      type="text"
                      fullWidth
                    />
                  </Box>

                  {!clientExists && !showNewClientFields && (
                    <Button
                      variant="contained"
                      onClick={handleAddNew}
                      sx={{
                        backgroundColor: '#6174DD',
                        height: '46px',
                        mb: '8px',
                      }}
                    >
                      Add New
                    </Button>
                  )}
                </Box>

                {clientExists && formData.clientNumber && (
                  <ClinetSuggestion
                    data={clientSuggested}
                    handleSuggestedClick={handleSuggestedClick}
                  />
                )}
              </Grid>

              {/* New Client Fields */}
              {showNewClientFields && !clientExists && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      bgcolor: '#fafafa',
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                      New Client Details
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextInputs
                          title="Client Name"
                          name="clientName"
                          placeholder="Enter client name"
                          value={formData.clientName}
                          handleChange={(e) => updateFormField('clientName', e.target.value)}
                          type="text"
                          fullWidth
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <RadioInputs
                          title="Gender"
                          name="gender"
                          value={formData.gender}
                          handleChange={(e) => updateFormField('gender', e.target.value)}
                          options={[
                            { title: 'Male', value: 'Male' },
                            { title: 'Female', value: 'Female' },
                          ]}
                          row
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              )}

              {/* Payment Section */}
              <Grid item xs={12}>
                {renderPaymentSection()}
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
            <Button
              variant="outlined"
              onClick={handleCloseDialogue}
              sx={{
                color: '#f44336',
                borderColor: '#f44336',
                '&:hover': {
                  borderColor: '#d32f2f',
                  backgroundColor: 'rgba(244, 67, 54, 0.04)',
                },
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={handleBuyPackage}
              disabled={loading}
              sx={{
                backgroundColor: '#6174DD',
                '&:hover': { backgroundColor: '#4a5abb' },
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
                  Processing...
                </>
              ) : (
                'Confirm Purchase'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bill Dialog */}
        <Dialog
          open={openBillDialogue}
          onClose={handleBillCloseDialogue}
          PaperProps={{
            style: {
              maxWidth: '1200px',
              width: '50%',
              minHeight: '40%',
            },
          }}
        >
          <DialogTitle>Bill Details</DialogTitle>
          <DialogContent>
            <DialogContentText>Here you can see Bill details.</DialogContentText>
            <Box sx={{ overflow: 'auto', width: { xs: '450px', sm: 'auto' } }}>
              <table className="table-container mt-3">
                <thead className="table-thead">
                  <tr>
                    {['Date of Bill', 'Client Name', 'Contact', 'Total', 'Paid', 'Pending'].map(
                      (h, index) => {
                        return (
                          <th key={index} style={{ width: '100%' }}>
                            {h}
                          </th>
                        );
                      },
                    )}
                  </tr>
                </thead>
                <tbody className="table-tbody">
                  <tr key={createdBill._id}>
                    <td style={{ width: '100%' }}>{createdBill?.dateOfBilling}</td>
                    <td style={{ width: '100%' }}>{createdBill?.clientName}</td>
                    <td style={{ width: '100%' }}>
                      {typeof branchData?.isMasked !== 'boolean' || !createdBill?.clientNumber
                        ? 'loading...'
                        : maskData(createdBill?.clientNumber, branchData?.isMasked)}
                    </td>
                    <td style={{ width: '100%' }}>{createdBill?.amountPayable}</td>
                    <td style={{ width: '100%' }}>
                      {createdBill?.amountPaid?.reduce(
                        (acc, payment) => acc + Number(payment.amount),
                        0,
                      )}
                    </td>
                    <td style={{ width: '100%' }}>0</td>
                  </tr>
                </tbody>
              </table>
            </Box>
          </DialogContent>
          <DialogActions sx={{ gap: '20px' }}>
            <Button
              variant="contained"
              color="primary"
              target="_blank"
              sx={{
                background: '#6174DD',
              }}
              onClick={handleBillCloseDialogue}
            >
              Not Now
            </Button>

            <Button
              variant="contained"
              color="primary"
              target="_blank"
              sx={{
                background: '#6174DD',
              }}
              onClick={() => navigate(`/Packagebilling/${createdBill?._id}`)}
            >
              View Bill
            </Button>
          </DialogActions>
        </Dialog>

        {loading && <ShowLoader />}
      </DashboardCard>
    </PageContainer>
  );
};

export default PackageDetails;
